<script lang="ts" setup>
import { gsap } from 'gsap'
import type { NavigationTab, NavigationTabPayload } from '@/types'

interface Props {
  currentTab: NavigationTab
  currentTabIndex: number
  isOpen: boolean
  offsetX: string
}

interface PanelLayer {
  id: number
  tab: NavigationTab
}

const emit = defineEmits<{
  (e: 'change-tab-state', payload: NavigationTabPayload): void
}>()

const props = defineProps<Props>()

const DURATION = {
  open: 0.3,
  close: 0.2,
  morph: 0.4,
  content: 0.3,
}

const CONTENT_SHIFT = 24

const panelRef = ref<HTMLElement | null>(null)
const layers = ref<PanelLayer[]>([])
const layerElements = new Map<number, HTMLElement>()

let nextLayerId = 0
let timeline: gsap.core.Timeline | null = null

const setLayerRef = (
  id: number,
  el: Element | ComponentPublicInstance | null,
) => {
  if (el instanceof HTMLElement) {
    layerElements.set(id, el)
    return
  }
  layerElements.delete(id)
}

const killTimeline = () => {
  timeline?.kill()
  timeline = null
}

const changeTab = () => {
  emit('change-tab-state', { event: { type: 'OPEN_NEXT' } })
}

const closePanel = () => {
  emit('change-tab-state', {
    event: {
      type: 'RESET',
    },
  })
}

const handleMouseLeave = (e: MouseEvent) => {
  const related = e.relatedTarget
  if (related instanceof Node) {
    const nav = document.getElementById('desktop-navigation')
    if (nav?.contains(related)) {
      return
    }
  }
  closePanel()
}

const keepLatestLayer = () => {
  const latest = layers.value.at(-1)
  if (!latest || layers.value.length === 1) {
    return
  }
  layers.value = [latest]
}

const openDropdown = async () => {
  if (!props.currentTab.sections.length) {
    return
  }

  killTimeline()
  layers.value = [{ id: ++nextLayerId, tab: props.currentTab }]
  await nextTick()

  const panel = panelRef.value
  if (!panel) {
    return
  }

  gsap.set(panel, {
    display: 'grid',
    left: props.offsetX,
    xPercent: -50,
    y: 0,
    scale: 1,
    width: 'auto',
    height: 'auto',
    transformOrigin: '50% 0%',
    pointerEvents: 'auto',
  })

  timeline = gsap.timeline()
  timeline.fromTo(
    panel,
    {
      autoAlpha: 0,
      y: -12,
      scale: 0.96,
    },
    {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      duration: DURATION.open,
      ease: 'power3.out',
    },
  )
}

const closeDropdown = () => {
  const panel = panelRef.value
  if (!panel) {
    return
  }

  killTimeline()
  timeline = gsap.timeline({
    onComplete: () => {
      layers.value = []
      gsap.set(panel, {
        pointerEvents: 'none',
        clearProps: 'width,height',
      })
    },
  })
  timeline.to(panel, {
    autoAlpha: 0,
    y: -8,
    scale: 0.98,
    duration: DURATION.close,
    ease: 'power2.in',
  })
}

const morphDropdown = async (direction: number) => {
  const panel = panelRef.value
  if (!panel || !props.currentTab.sections.length) {
    return
  }

  killTimeline()
  keepLatestLayer()
  await nextTick()

  const outgoing = layers.value[0]
    ? layerElements.get(layers.value[0].id)
    : undefined
  const oldWidth = panel.offsetWidth
  const oldHeight = panel.offsetHeight

  if (outgoing) {
    gsap.set(outgoing, {
      position: 'absolute',
      top: 0,
      left: 0,
      width: outgoing.offsetWidth,
      pointerEvents: 'none',
    })
  }

  const incomingId = ++nextLayerId
  layers.value.push({ id: incomingId, tab: props.currentTab })
  await nextTick()

  const incoming = layerElements.get(incomingId)
  if (!incoming) {
    return
  }

  gsap.set(panel, { width: 'auto', height: 'auto' })
  const newWidth = panel.offsetWidth
  const newHeight = panel.offsetHeight
  gsap.set(panel, { width: oldWidth, height: oldHeight })

  timeline = gsap.timeline({
    onComplete: () => {
      keepLatestLayer()
      gsap.set(panel, { clearProps: 'width,height' })
    },
  })

  timeline.to(
    panel,
    {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      left: props.offsetX,
      width: newWidth,
      height: newHeight,
      duration: DURATION.morph,
      ease: 'power3.out',
    },
    0,
  )

  if (outgoing) {
    timeline.to(
      outgoing,
      {
        autoAlpha: 0,
        x: -CONTENT_SHIFT * direction,
        duration: DURATION.content,
        ease: 'power2.out',
      },
      0,
    )
  }

  timeline.fromTo(
    incoming,
    {
      autoAlpha: 0,
      x: CONTENT_SHIFT * direction,
    },
    {
      autoAlpha: 1,
      x: 0,
      duration: DURATION.content,
      ease: 'power2.out',
    },
    0.05,
  )
}

watch(
  () => [props.isOpen, props.currentTabIndex] as const,
  async ([isOpen, index], previous) => {
    if (!import.meta.client) {
      return
    }

    const wasOpen = previous?.[0] ?? false
    const prevIndex = previous?.[1] ?? index

    if (isOpen && !wasOpen) {
      await openDropdown()
      return
    }

    if (!isOpen && wasOpen) {
      closeDropdown()
      return
    }

    if (isOpen && index !== prevIndex) {
      await morphDropdown(index > prevIndex ? 1 : -1)
    }
  },
  { flush: 'post' },
)

onUnmounted(() => {
  killTimeline()
  layerElements.clear()
})
</script>

<template>
  <div
    id="navigation-tab-panel"
    ref="panelRef"
    role="tabpanel"
    class="navigation-tab-panel"
    :aria-hidden="!isOpen"
    @keydown.esc="closePanel"
    @mouseleave="handleMouseLeave"
  >
    <div class="navigation-tab-panel__viewport">
      <div
        v-for="layer in layers"
        :key="layer.id"
        :ref="(el) => setLayerRef(layer.id, el)"
        class="navigation-tab-panel__layer"
      >
        <div class="padding-flow">
          <div
            v-for="section in layer.tab.sections"
            :key="section.title"
            class="navigation-tab-panel__section"
          >
            <p
              v-if="layer.tab.sections.length > 1"
              class="heading--6 navigation-tab-panel__title heading-6"
            >
              {{ $t(`common.navigation.sections_names.${section.title}`) }}
            </p>
            <ul class="flow | navigation-tab-panel__list">
              <NavigationLink
                v-for="(link, linkIndex) in section.links"
                :key="linkIndex"
                :link="link"
                @close-tab="closePanel"
                @change-tab="changeTab"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.navigation-tab-panel {
  @include shadow-flat-strong;

  --link-padding-top: 8px;
  --link-padding-bottom: 8px;
  --link-padding-inline: 12px;
  --padding-block-start: 24px;
  --padding-block-end: calc(32px - var(--link-padding-bottom));
  --padding-inline: 36px;

  display: grid;
  overflow: hidden;
  position: absolute;
  z-index: 9998;
  left: 0;
  top: calc(100% - 8px);
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  background-color: var(--white);
  border-radius: var(--border-radius-extra-large);
  padding: var(--padding-block-end) 0;
  width: max-content;
  min-width: 218px;

  &__viewport {
    position: relative;
  }

  &__layer {
    width: max-content;
    min-width: 218px;
  }

  &__list {
    padding: 0 calc(var(--padding-inline) - var(--link-padding-inline));

    --space: 4px;
  }

  &__title {
    padding: 0 var(--padding-inline);
    margin: 0;
    color: var(--cobalt-800);
    margin-bottom: 12px;
  }

  &__section {
    --space: 32px;
  }
}
</style>
